import React, { useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../../shared/components/AppComponents';
import { apiFetchJson } from '../../apiClient';
import ExpenseCharts from './ExpenseCharts';
import ExpenseSettlement from './ExpenseSettlement';
import ExpenseInsightsPanel from './ExpenseInsightsPanel';
import './ExpenseTracker.css';

const DEFAULT_OWNERSHIP_LABELS = { 0: 'Common', 1: 'Person 1', 2: 'Person 2' };

function ExpenseTracker({ user }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('dashboard');
  const [period, setPeriod] = useState(getDefaultPeriod());
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [inconsistencies, setInconsistencies] = useState([]);
  const [insights, setInsights] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', icon: '📦', color: '#6e7785', open: false });
  const [manualEntry, setManualEntry] = useState({ open: false, saving: false, error: null });
  const [ownershipLabels, setOwnershipLabels] = useState(DEFAULT_OWNERSHIP_LABELS);

  // Upload state
  const [uploadState, setUploadState] = useState({
    file: null,
    preview: null,
    loading: false,
    error: null,
    bank: null,
    dragOver: false
  });
  const [importResult, setImportResult] = useState(null);

  function getDefaultPeriod() {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
  }

  // Unwrap $values from ReferenceHandler.Preserve responses
  const unwrap = (data) => {
    const arr = Array.isArray(data) ? data : (data?.$values || []);
    return arr.filter(item => item && !item.$ref);
  };

  const loadCategories = useCallback(async () => {
    try {
      const data = unwrap(await apiFetchJson(instance, '/api/Expenses/categories'));
      setCategories(data);
      if (data.length <= 1) {
        await apiFetchJson(instance, '/api/Expenses/categories/seed', { method: 'POST' });
        setCategories(unwrap(await apiFetchJson(instance, '/api/Expenses/categories')));
      }
    } catch (err) { console.error('Failed to load categories:', err); }
  }, [instance]);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      setTransactions(unwrap(await apiFetchJson(instance, `/api/Expenses/transactions?period=${period}`)));
    } catch (err) { console.error('Failed to load transactions:', err); }
    setLoading(false);
  }, [instance, period]);

  const loadBatches = useCallback(async () => {
    try {
      setBatches(unwrap(await apiFetchJson(instance, `/api/Expenses/batches?period=${period}`)));
    } catch (err) { console.error('Failed to load batches:', err); }
  }, [instance, period]);

  const loadInsights = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, `/api/Expenses/insights?period=${period}`);
      setInsights(data);
    } catch (err) {
      console.error('Failed to load insights:', err);
      setInsights(null);
    }
  }, [instance, period]);

  const loadSuggestions = useCallback(async () => {
    try {
      setSuggestions(unwrap(await apiFetchJson(instance, '/api/Expenses/suggestions')));
    } catch (err) { console.error('Failed to load suggestions:', err); }
  }, [instance]);

  const loadInconsistencies = useCallback(async () => {
    try {
      setInconsistencies(unwrap(await apiFetchJson(instance, '/api/Expenses/inconsistencies')));
    } catch (err) { console.error('Failed to load inconsistencies:', err); }
  }, [instance]);

  const loadOwnershipLabels = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/Expenses/ownership-labels');
      if (data?.labels) setOwnershipLabels(data.labels);
    } catch (err) { console.error('Failed to load ownership labels:', err); }
  }, [instance]);

  useEffect(() => {
    loadCategories();
    loadSuggestions();
    loadInconsistencies();
    loadOwnershipLabels();
  }, [loadCategories, loadSuggestions, loadInconsistencies, loadOwnershipLabels]);

  useEffect(() => {
    if (activeView === 'approve' || activeView === 'dashboard') {
      loadTransactions();
      loadBatches();
    }
    if (activeView === 'dashboard') {
      loadInsights();
    }
  }, [activeView, period, loadTransactions, loadBatches, loadInsights]);

  // Approval handlers
  const handleApprove = async (txnId, categoryId, ownership, isHidden = false) => {
    try {
      await apiFetchJson(instance, `/api/Expenses/transactions/${txnId}/approve`, {
        method: 'PUT',
        body: JSON.stringify({ categoryId, ownership, isHidden })
      });
      setTransactions(prev => prev.map(t =>
        t.id === txnId ? { ...t, isApproved: true, ownership, isHidden,
          category: categories.find(c => c.id === categoryId) } : t
      ));
    } catch (err) { console.error('Failed to approve:', err); }
  };

  const handleApproveAll = async () => {
    const pending = transactions.filter(t => !t.isApproved && t.editCategory);
    if (pending.length === 0) return;

    try {
      await apiFetchJson(instance, '/api/Expenses/transactions/approve-bulk', {
        method: 'PUT',
        body: JSON.stringify({
          transactionIds: pending.map(t => t.id),
          approvals: pending.map(t => ({
            transactionId: t.id,
            categoryId: t.editCategory,
            ownership: t.editOwnership ?? 0,
            isHidden: t.editHidden || false
          }))
        })
      });
      await loadTransactions();
      await loadSuggestions();
    } catch (err) { console.error('Failed to bulk approve:', err); }
  };

  const updateTransactionEdit = (txnId, field, value) => {
    setTransactions(prev => prev.map(t =>
      t.id === txnId ? { ...t, [field]: value } : t
    ));
  };

  // Auto-apply suggestions to unapproved transactions; default ownership to Common
  const applySuggestions = useCallback(() => {
    setTransactions(prev => prev.map(t => {
      if (t.isApproved) return t;
      // Default ownership to Common (0) if not already set
      const defaultOwnership = t.editOwnership ?? t.ownership ?? 0;
      if (suggestions.length === 0) {
        return t.editOwnership !== undefined ? t : { ...t, editOwnership: defaultOwnership };
      }
      if (t.editCategory) return t.editOwnership !== undefined ? t : { ...t, editOwnership: defaultOwnership };
      const match = suggestions.find(s => s.description === t.description.toLowerCase());
      if (!match) return { ...t, editOwnership: defaultOwnership };
      return { ...t, editCategory: match.categoryId, editOwnership: match.ownership ?? defaultOwnership, suggested: true };
    }));
  }, [suggestions]);

  useEffect(() => {
    applySuggestions();
  }, [transactions.length, suggestions, applySuggestions]);

  const handleUnapprove = async (txnId) => {
    try {
      await apiFetchJson(instance, `/api/Expenses/transactions/${txnId}/unapprove`, { method: 'PUT' });
      setTransactions(prev => prev.map(t =>
        t.id === txnId ? { ...t, isApproved: false, editCategory: '', editOwnership: undefined } : t
      ));
    } catch (err) { console.error('Failed to unapprove:', err); }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) return;
    try {
      await apiFetchJson(instance, '/api/Expenses/categories', {
        method: 'POST',
        body: JSON.stringify({ name: newCategory.name, icon: newCategory.icon, color: newCategory.color })
      });
      await loadCategories();
      setNewCategory({ name: '', icon: '📦', color: '#6e7785', open: false });
    } catch (err) {
      alert(err.message || 'Failed to create category');
      console.error('Failed to create category:', err);
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const description = (form.get('description') || '').toString().trim();
    const amount = parseFloat(form.get('amount'));
    const categoryId = form.get('categoryId');
    const date = form.get('date');

    if (!description || !categoryId || !(amount > 0)) {
      setManualEntry(prev => ({ ...prev, error: t('expenses.manual.invalid') }));
      return;
    }

    setManualEntry(prev => ({ ...prev, saving: true, error: null }));
    try {
      await apiFetchJson(instance, '/api/Expenses/transactions', {
        method: 'POST',
        body: JSON.stringify({
          description,
          amount,
          categoryId,
          transactionDate: date,
          ownership: parseInt(form.get('ownership'), 10) || 0,
          currency: 'NOK',
          period
        })
      });
      e.target.reset();
      setManualEntry({ open: false, saving: false, error: null });
      await loadTransactions();
    } catch (err) {
      console.error('Failed to create transaction:', err);
      setManualEntry(prev => ({ ...prev, saving: false, error: err.message || t('expenses.manual.failed') }));
    }
  };

  const handleMoveBatch = async (batchId, newPeriod) => {
    try {
      await apiFetchJson(instance, `/api/Expenses/batches/${batchId}/move`, {
        method: 'PUT',
        body: JSON.stringify({ newPeriod })
      });
      await loadBatches();
      await loadTransactions();
    } catch (err) { console.error('Failed to move batch:', err); }
  };

  const handleRecategorize = async (transactionIds, categoryId) => {
    try {
      await apiFetchJson(instance, '/api/Expenses/transactions/recategorize', {
        method: 'PUT',
        body: JSON.stringify({ transactionIds, categoryId })
      });
      await loadTransactions();
      await loadInconsistencies();
      await loadSuggestions();
    } catch (err) { console.error('Failed to recategorize:', err); }
  };

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState(prev => ({ ...prev, dragOver: false }));
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (file) handleFileSelected(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelected = async (file) => {
    if (!file.name.endsWith('.xlsx')) {
      setUploadState(prev => ({ ...prev, error: t('expenses.xlsxOnly') }));
      return;
    }

    setUploadState(prev => ({ ...prev, file, loading: true, error: null, preview: null }));

    const formData = new FormData();
    formData.append('file', file);

    try {
      const bankParam = uploadState.bank ? `?bank=${encodeURIComponent(uploadState.bank)}` : '';
      const preview = await apiFetchJson(instance, `/api/Expenses/upload${bankParam}`, {
        method: 'POST',
        body: formData
      });

      // Unwrap $values from nested arrays
      preview.cardHolders = unwrap(preview.cardHolders).map(ch => ({
        ...ch,
        transactions: unwrap(ch.transactions)
      }));
      if (preview.detectedPeriod) setPeriod(preview.detectedPeriod);
      setUploadState(prev => ({ ...prev, loading: false, preview }));
    } catch (err) {
      setUploadState(prev => ({ ...prev, loading: false, error: err.message }));
    }
  };

  const handleImport = async (cardHolder) => {
    setUploadState(prev => ({ ...prev, loading: true }));

    try {
      const result = await apiFetchJson(instance, '/api/Expenses/import', {
        method: 'POST',
        body: JSON.stringify({
          fileName: uploadState.preview.fileName,
          bankName: uploadState.preview.bankName,
          cardHolderName: cardHolder?.cardHolderName,
          settlementPeriod: period,
          allowDuplicates: false,
          transactions: cardHolder.transactions
        })
      });

      setUploadState(prev => ({ ...prev, loading: false }));
      return { ...result, cardHolderName: cardHolder?.cardHolderName };
    } catch (err) {
      setUploadState(prev => ({ ...prev, loading: false, error: err.message }));
      return null;
    }
  };

  const handleImportAll = async () => {
    const results = [];
    for (const cardHolder of uploadState.preview.cardHolders) {
      const result = await handleImport(cardHolder);
      if (result) results.push(result);
    }
    setImportResult(results.length > 0 ? results : null);
  };

  const resetUpload = () => {
    setUploadState({
      file: null, preview: null, loading: false, error: null,
      bank: null, dragOver: false
    });
    setImportResult(null);
  };

  const [mgmtOpen, setMgmtOpen] = useState(false);

  const ownershipOptions = [
    { value: 0, label: ownershipLabels[0] || 'Common', emoji: '👫' },
    { value: 1, label: ownershipLabels[1] || 'Person 1', emoji: '👩' },
    { value: 2, label: ownershipLabels[2] || 'Person 2', emoji: '👨' }
  ];

  const renderDashboard = () => {
    const unapproved = transactions.filter(t => !t.isApproved);
    const approved = transactions.filter(t => t.isApproved);
    const periodLabel = new Date(period + '-01').toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' });

    return (
      <div className="expense-dashboard">
        {/* Period selector */}
        <div className="expense-period-bar">
          <button className="expense-btn secondary" aria-label="Previous period" onClick={() => {
            const d = new Date(period + '-01');
            d.setMonth(d.getMonth() - 1);
            setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }}>◄</button>
          <h2>{periodLabel}</h2>
          <button className="expense-btn secondary" aria-label="Next period" onClick={() => {
            const d = new Date(period + '-01');
            d.setMonth(d.getMonth() + 1);
            setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }}>►</button>
          <button className="expense-btn settlement-btn" onClick={() => setActiveView('settlement')}>
            💸 {t('expenses.settlement')}
          </button>
          <button
            className="expense-btn primary"
            onClick={() => setManualEntry(prev => ({ ...prev, open: !prev.open, error: null }))}
          >
            + {t('expenses.manual.add')}
          </button>
        </div>

        {/* Manual entry — cash spending never shows up in a bank import */}
        {manualEntry.open && (
          <form className="expense-manual-form" onSubmit={handleCreateTransaction}>
            <div className="expense-manual-row">
              <input
                name="description"
                className="expense-manual-desc"
                placeholder={t('expenses.manual.descPlaceholder')}
                required
              />
              <input
                name="amount"
                type="number"
                step="0.01"
                min="0.01"
                className="expense-manual-amount"
                placeholder={t('expenses.manual.amountPlaceholder')}
                required
              />
              <input
                name="date"
                type="date"
                defaultValue={`${period}-01`}
                required
              />
            </div>
            <div className="expense-manual-row">
              <select name="categoryId" required defaultValue="">
                <option value="" disabled>{t('expenses.manual.chooseCategory')}</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                ))}
              </select>
              <select name="ownership" defaultValue="0">
                {ownershipOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.emoji} {o.label}</option>
                ))}
              </select>
              <button type="submit" className="expense-btn primary" disabled={manualEntry.saving}>
                {manualEntry.saving ? t('common.saving') : t('common.save')}
              </button>
              <button
                type="button"
                className="expense-btn secondary"
                onClick={() => setManualEntry({ open: false, saving: false, error: null })}
              >
                {t('common.cancel')}
              </button>
            </div>
            {manualEntry.error && (
              <div className="expense-manual-error" role="alert">{manualEntry.error}</div>
            )}
          </form>
        )}

        {/* Alert: unapproved transactions */}
        {unapproved.length > 0 && (
          <div className="expense-alert-banner">
            <span>📋 {t('expenses.needsReview', { count: unapproved.length })}</span>
            <button className="expense-btn primary" onClick={() => setActiveView('approve')}>{t('expenses.reviewNow')}</button>
          </div>
        )}

        {/* Inconsistency warnings */}
        {inconsistencies.length > 0 && (
          <div className="expense-inconsistency-banner">
            <h4>⚠️ {t('expenses.categoryInconsistencies')}</h4>
            <p>{t('expenses.sameStoreDifferent')}</p>
            {inconsistencies.map((inc, i) => (
              <div key={i} className="expense-inconsistency-item">
                <strong>"{inc.description}"</strong> is in:
                {unwrap(inc.categories).map((cat, j) => (
                  <span key={j} className="expense-inconsistency-cat">
                    {cat.icon} {cat.name} ({cat.count}×)
                    {j < unwrap(inc.categories).length - 1 && (
                      <button
                        className="expense-btn-sm primary"
                        title={`Move all to ${cat.name}`}
                        onClick={() => {
                          const otherIds = unwrap(inc.categories)
                            .filter((_, k) => k !== j)
                            .flatMap(c => unwrap(c.transactionIds));
                          handleRecategorize(otherIds, cat.categoryId);
                        }}
                      >{t('expenses.useThis')}</button>
                    )}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Charts — the main content */}
        {loading ? (
          <div className="expense-loading"><div className="expense-spinner" /><p>{t('common.loading')}</p></div>
        ) : (
          <>
            <ExpenseInsightsPanel
              period={period}
              insights={insights}
              categories={categories}
            />
            <ExpenseCharts transactions={transactions} categories={categories} period={period} ownershipLabels={ownershipLabels} />
          </>
        )}

        {/* Collapsible management section */}
        <div className="expense-mgmt-section">
          <button className="expense-mgmt-toggle" onClick={() => setMgmtOpen(!mgmtOpen)} aria-expanded={mgmtOpen}>
            {mgmtOpen ? '▼' : '►'} {t('expenses.manageUploads')}
            <span className="expense-mgmt-summary">
              {batches.length} batch{batches.length !== 1 ? 'es' : ''} · {approved.length} approved · {unapproved.length} pending
            </span>
          </button>

          {mgmtOpen && (
            <div className="expense-mgmt-content">
              <div className="expense-mgmt-actions">
                <button className="expense-btn primary" onClick={() => setActiveView('upload')}>📤 {t('expenses.uploadStatement')}</button>
                <button className="expense-btn secondary" onClick={() => setActiveView('approve')}>📋 {t('expenses.reviewTransactions', { count: transactions.length })}</button>
              </div>

              {/* Batch list */}
              {batches.length > 0 && (
                <div className="expense-batch-list">
                  {batches.map(b => (
                    <div key={b.id} className="expense-batch-item">
                      <div className="expense-batch-info">
                        <strong>{b.fileName}</strong>
                        <span>{b.bankName} · {b.cardHolderName || 'Unknown'} · {b.transactionCount} txns · <strong>{(b.totalAmount || 0).toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr</strong></span>
                        <span className="expense-batch-meta">Uploaded by {b.uploadedBy} · {new Date(b.importedAt).toLocaleDateString('nb-NO')}</span>
                      </div>
                      <div className="expense-batch-actions">
                        <label>{t('expenses.period')}</label>
                        <input
                          type="month"
                          defaultValue={b.settlementPeriod}
                          className="expense-batch-period-input"
                          onChange={(e) => {
                            if (e.target.value && e.target.value !== b.settlementPeriod) {
                              if (window.confirm(`Move "${b.fileName}" from ${b.settlementPeriod} to ${e.target.value}?`)) {
                                handleMoveBatch(b.id, e.target.value);
                              } else {
                                e.target.value = b.settlementPeriod;
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderApprovalInbox = () => {
    const unapproved = transactions.filter(t => !t.isApproved);
    const approvedTxns = transactions.filter(t => t.isApproved);
    const allHaveEdits = unapproved.length > 0 && unapproved.every(t => t.editCategory);
    const periodLabel = new Date(period + '-01').toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' });

    return (
      <div className="expense-approval">
        <div className="expense-upload-header">
          <button className="expense-btn secondary" onClick={() => setActiveView('dashboard')}>← {t('common.back')}</button>
          <h2>{t('expenses.transactions')}</h2>
        </div>

        {/* Period navigation */}
        <div className="expense-period-bar">
          <button className="expense-btn secondary" onClick={() => {
            const d = new Date(period + '-01');
            d.setMonth(d.getMonth() - 1);
            setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }}>◄</button>
          <h3>{periodLabel}</h3>
          <button className="expense-btn secondary" onClick={() => {
            const d = new Date(period + '-01');
            d.setMonth(d.getMonth() + 1);
            setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }}>►</button>
        </div>

        {/* Add category */}
        <div className="expense-category-bar">
          {newCategory.open ? (
            <div className="expense-new-category-form">
              <input placeholder={t('expenses.categoryName')} value={newCategory.name}
                onChange={e => setNewCategory(prev => ({ ...prev, name: e.target.value }))} aria-label={t('expenses.categoryName')} />
              <input placeholder={t('expenses.iconLabel')} value={newCategory.icon} style={{ width: 50 }}
                onChange={e => setNewCategory(prev => ({ ...prev, icon: e.target.value }))} aria-label={t('expenses.iconLabel')} />
              <input type="color" value={newCategory.color}
                onChange={e => setNewCategory(prev => ({ ...prev, color: e.target.value }))} aria-label="Category color" />
              <button className="expense-btn-sm primary" onClick={handleCreateCategory}>Add</button>
              <button className="expense-btn-sm secondary" onClick={() => setNewCategory(prev => ({ ...prev, open: false }))}>Cancel</button>
            </div>
          ) : (
            <button className="expense-btn secondary" onClick={() => setNewCategory(prev => ({ ...prev, open: true }))}>
              {t('expenses.addCategory')}
            </button>
          )}
          {unapproved.length > 0 && (
            <button
              className="expense-btn primary"
              onClick={handleApproveAll}
              disabled={!allHaveEdits}
              title={allHaveEdits ? t('expenses.approveAll', { count: unapproved.length }) : t('expenses.setCategoryFirst')}
            >
              ✅ {t('expenses.approveAll', { count: unapproved.length })}
            </button>
          )}
        </div>

        {loading && <div className="expense-loading"><div className="expense-spinner" /><p>{t('common.loading')}</p></div>}

        {/* Unapproved */}
        {unapproved.length > 0 && (
          <div className="expense-section">
            <h3 className="expense-section-title">🔴 {t('expenses.needsReviewCount', { count: unapproved.length })}</h3>
            <div className="expense-preview-table-wrapper">
              <table className="expense-preview-table">
                <thead>
                  <tr>
                    <th>{t('expenses.date')}</th>
                    <th>{t('expenses.description')}</th>
                    <th>{t('expenses.location')}</th>
                    <th>{t('expenses.cardholder')}</th>
                    <th className="amount-col">{t('expenses.amount')}</th>
                    <th>{t('expenses.category')}</th>
                    <th>{t('expenses.ownership')}</th>
                    <th>🔒</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {unapproved.map(txn => (
                    <tr key={txn.id} className={txn.suggested ? 'suggested-row' : ''}>
                      <td>{txn.date}</td>
                      <td className="expense-desc-cell">{txn.description}</td>
                      <td>{txn.location || '—'}</td>
                      <td className="expense-cardholder-cell">{txn.cardHolderName || '—'}</td>
                      <td className="amount-col">{txn.amount.toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr</td>
                      <td>
                        <select
                          className={`expense-inline-select${txn.suggested && txn.editCategory ? ' suggested' : ''}`}
                          value={txn.editCategory || ''}
                          onChange={(e) => updateTransactionEdit(txn.id, 'editCategory', e.target.value)}
                        >
                          <option value="">{t('expenses.select')}</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className={`expense-inline-select${txn.suggested && txn.editOwnership !== undefined ? ' suggested' : ''}`}
                          value={txn.editOwnership ?? 0}
                          onChange={(e) => updateTransactionEdit(txn.id, 'editOwnership', parseInt(e.target.value))}
                        >
                          {ownershipOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.emoji} {o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={txn.editHidden || false}
                          onChange={(e) => updateTransactionEdit(txn.id, 'editHidden', e.target.checked)}
                          title={t('expenses.hideFromPartner')}
                        />
                      </td>
                      <td>
                        <button
                          className="expense-btn-sm primary"
                          disabled={!txn.editCategory}
                          onClick={() => handleApprove(txn.id, txn.editCategory, txn.editOwnership ?? 0, txn.editHidden)}
                        >✅</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="expense-sum-row">
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>{t('expenses.sum')}</td>
                    <td className="amount-col" style={{ fontWeight: 'bold' }}>
                      {unapproved.reduce((sum, t) => sum + t.amount, 0).toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr
                    </td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Approved */}
        {approvedTxns.length > 0 && (
          <div className="expense-section">
            <h3 className="expense-section-title">🟢 {t('expenses.approvedCount', { count: approvedTxns.length })}</h3>
            <div className="expense-preview-table-wrapper">
              <table className="expense-preview-table">
                <thead>
                  <tr>
                    <th>{t('expenses.date')}</th>
                    <th>{t('expenses.description')}</th>
                    <th className="amount-col">{t('expenses.amount')}</th>
                    <th>{t('expenses.category')}</th>
                    <th>{t('expenses.ownership')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {approvedTxns.map(txn => (
                    <tr key={txn.id} className="approved-row">
                      <td>{txn.date}</td>
                      <td>{txn.isHidden ? `🔒 ${t('expenses.hidden')}` : txn.description}</td>
                      <td className="amount-col">{txn.isHidden ? '—' : `${txn.amount.toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr`}</td>
                      <td>{txn.category ? `${txn.category.icon} ${txn.category.name}` : '—'}</td>
                      <td>{ownershipOptions.find(o => o.value === txn.ownership)?.label || '—'}</td>
                      <td>
                        <button
                          className="expense-btn-sm secondary"
                          onClick={() => handleUnapprove(txn.id)}
                          title="Edit this transaction"
                        >✏️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="expense-sum-row">
                    <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>{t('expenses.sum')}</td>
                    <td className="amount-col" style={{ fontWeight: 'bold' }}>
                      {approvedTxns.filter(t => !t.isHidden).reduce((sum, t) => sum + t.amount, 0).toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Grand total */}
        {transactions.length > 0 && (
          <div className="expense-grand-total">
            <strong>{t('expenses.totalTransactions', { count: transactions.length })}</strong>
            <span className="expense-grand-total-amount">
              {transactions.reduce((sum, t) => sum + t.amount, 0).toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr
            </span>
          </div>
        )}

        {!loading && transactions.length === 0 && (
          <div className="expense-empty">
            <p>{t('expenses.noTransactions')}</p>
          </div>
        )}
      </div>
    );
  };

  const renderUpload = () => (
    <div className="expense-upload">
      <div className="expense-upload-header">
        <button className="expense-btn secondary" onClick={() => { resetUpload(); setActiveView('dashboard'); }}>
          ← {t('common.back')}
        </button>
        <h2>{t('expenses.uploadStatement')}</h2>
      </div>

      {/* Month selector */}
      <div className="expense-period-selector">
        <label>{t('expenses.settlementPeriodLabel')}</label>
        <input
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        />
      </div>

      {/* Import result */}
      {importResult && (
        <div className="expense-import-result success" role="status" aria-live="polite">
          <h3>✅ {t('expenses.importComplete')}</h3>
          {Array.isArray(importResult) ? importResult.map((r, i) => (
            <p key={i}>
              {r.cardHolderName && <strong>{r.cardHolderName}: </strong>}
              {t('expenses.transactionsImported', { count: r.imported })}
              {r.autoCategorized > 0 && ` · 🧠 ${t('expenses.autoCategorizedCount', { count: r.autoCategorized })}`}
              {r.duplicatesSkipped > 0 && ` · ⚠️ ${t('expenses.duplicatesSkippedCount', { count: r.duplicatesSkipped })}`}
            </p>
          )) : (
            <p>{t('expenses.transactionsImportedForPeriod', { count: importResult.imported, period: importResult.settlementPeriod })}
              {importResult.autoCategorized > 0 && ` · 🧠 ${t('expenses.autoCategorizedCount', { count: importResult.autoCategorized })}`}
              {importResult.duplicatesSkipped > 0 && ` · ⚠️ ${t('expenses.duplicatesSkippedCount', { count: importResult.duplicatesSkipped })}`}
            </p>
          )}
          <button className="expense-btn primary" onClick={resetUpload}>{t('expenses.uploadAnother')}</button>
        </div>
      )}

      {/* File drop zone */}
      {!uploadState.preview && !importResult && (
        <div
          className={`expense-dropzone ${uploadState.dragOver ? 'drag-over' : ''}`}
          onDrop={handleFileDrop}
          onDragOver={(e) => { e.preventDefault(); setUploadState(prev => ({ ...prev, dragOver: true })); }}
          onDragLeave={() => setUploadState(prev => ({ ...prev, dragOver: false }))}
          onClick={() => document.getElementById('file-input').click()}
          role="button"
          tabIndex={0}
          aria-label={t('expenses.dragDropHere')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); document.getElementById('file-input').click(); } }}
        >
          <input
            id="file-input"
            type="file"
            accept=".xlsx"
            onChange={(e) => handleFileSelected(e.target.files[0])}
            style={{ display: 'none' }}
          />
          {uploadState.loading ? (
            <div className="expense-loading">
              <div className="expense-spinner" />
              <p>{t('expenses.parsingFile')}</p>
            </div>
          ) : (
            <>
              <span className="expense-dropzone-icon">📎</span>
              <p>{t('expenses.dragDropHere')}</p>
              <p className="expense-dropzone-hint">{t('expenses.clickToBrowse')}</p>
            </>
          )}
        </div>
      )}

      {/* Error */}
      {uploadState.error && (
        <div className="expense-error" role="alert">⚠️ {uploadState.error}</div>
      )}

      {/* Preview */}
      {uploadState.preview && !importResult && (
        <div className="expense-preview">
          <div className="expense-preview-header">
            <div>
              <h3>📄 {uploadState.preview.fileName}</h3>
              <p className="expense-bank-badge">
                {t('expenses.detected')} <strong>{uploadState.preview.bankName}</strong> ✓
              </p>
            </div>
            <button
              className="expense-btn primary"
              onClick={handleImportAll}
              disabled={uploadState.loading}
            >
              {uploadState.loading ? t('expenses.importing') : t('expenses.importAll')}
            </button>
          </div>

          {uploadState.preview.cardHolders.map((ch, i) => (
            <div key={i} className="expense-cardholder-section">
              <div className="expense-cardholder-header">
                <h4>
                  {ch.cardHolderName || 'Cardholder'}
                  {ch.cardNumber && <span className="expense-card-number"> ({ch.cardNumber})</span>}
                </h4>
                <div className="expense-cardholder-actions">
                  <span className="expense-txn-count">{ch.transactionCount} transactions</span>
                  <button
                    className="expense-btn-sm primary"
                    onClick={async () => {
                      const result = await handleImport(ch);
                      if (result) setImportResult(prev => [...(prev || []), result]);
                    }}
                    disabled={uploadState.loading}
                  >{t('expenses.importBtn')}</button>
                </div>
              </div>

              <div className="expense-preview-table-wrapper">
                <table className="expense-preview-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Location</th>
                      <th className="amount-col">Amount</th>
                      <th>Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ch.transactions.map((txn, j) => (
                      <tr key={j}>
                        <td>{txn.date}</td>
                        <td>{txn.description}</td>
                        <td>{txn.location || '—'}</td>
                        <td className="amount-col">{txn.amount.toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr</td>
                        <td>{txn.currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="expense-tracker">
      <AppHeader title={t('expenses.title')} icon="💰" user={user} />
      {activeView === 'dashboard' && renderDashboard()}
      {activeView === 'upload' && renderUpload()}
      {activeView === 'approve' && renderApprovalInbox()}
      {activeView === 'settlement' && (
        <ExpenseSettlement period={period} onClose={() => setActiveView('dashboard')} />
      )}
    </div>
  );
}

export default ExpenseTracker;
